import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Upload, Table, Button, Popconfirm, Spin, Typography, message, Empty, Space } from 'antd';
import { InboxOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { CrmAttachment, CrmEntityType } from '../../api/CRMApiClient';
import { formatFileSize, validateCrmFile, CRM_MAX_FILE_MB, CRM_MAX_VIDEO_MB, isCrmVideo, CRM_ACCEPT_ATTR } from '../../helpers/crmAttachmentRules';
import { formatHistoryDate } from '../../helpers/crmHistoryFormat';

const { Dragger } = Upload;
const { Text } = Typography;

interface Props {
  entityType: CrmEntityType;
  entityId: number;
  /** Load when the tab becomes visible. */
  active: boolean;
  /** Hide upload/delete controls (e.g. for read-only viewers). Default false. */
  readOnly?: boolean;
}

const CrmAttachmentsTab: React.FC<Props> = observer(({ entityType, entityId, active, readOnly = false }) => {
  const { t } = useTranslation();
  const { crmStore } = useStore();
  const [rows, setRows] = useState<CrmAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await crmStore.getCrmAttachments(entityType, entityId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, entityType, entityId]);

  const upload = async (file: File): Promise<boolean> => {
    setUploadingCount(c => c + 1);
    try {
      const result = await crmStore.uploadCrmAttachment(entityType, entityId, file);
      if (result.ok) {
        message.success(`${file.name}: ${t('UploadSuccess')}`);
        setRows(prev => [result.attachment, ...prev]);
        return true;
      }
      message.error(`${file.name}: ${t(result.code, { size: isCrmVideo(file.name) ? CRM_MAX_VIDEO_MB : CRM_MAX_FILE_MB })}`);
      return false;
    } finally {
      setUploadingCount(c => c - 1);
    }
  };

  const remove = async (att: CrmAttachment) => {
    const ok = await crmStore.deleteCrmAttachment(att.id);
    if (ok) {
      message.success(t('AttachmentDeleted'));
      setRows(prev => prev.filter(r => r.id !== att.id));
    } else {
      message.error(t('FailedToDeleteAttachment'));
    }
  };

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'fileName',
      key: 'fileName',
      render: (name: string, r: CrmAttachment) => (
        <a href={r.secureUrl} target="_blank" rel="noopener noreferrer">{name}</a>
      ),
    },
    { title: t('FileSize'), dataIndex: 'sizeBytes', key: 'size', width: 110, render: (b: number) => formatFileSize(b) },
    { title: t('UploadedBy'), dataIndex: 'uploadedByName', key: 'by', width: 180, render: (n?: string) => n || t('Unknown') },
    { title: t('UploadedAt'), dataIndex: 'uploadedAt', key: 'at', width: 160, render: (d: string) => formatHistoryDate(d) },
    {
      title: t('Actions'),
      key: 'actions',
      width: 140,
      render: (_: unknown, r: CrmAttachment) => (
        <Space size="small">
          <Button
            className="crm-action-btn crm-action-btn-edit"
            icon={<DownloadOutlined />}
            onClick={() => window.open(r.secureUrl, '_blank', 'noopener')}
          />
          {!readOnly && (
            <Popconfirm
              title={t('ConfirmDeleteAttachment')}
              description={t('AreYouSureDeleteAttachment')}
              okText={t('Delete')}
              cancelText={t('Cancel')}
              okButtonProps={{ danger: true }}
              onConfirm={() => remove(r)}
            >
              <Button className="crm-action-btn crm-action-btn-delete" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading || uploadingCount > 0}>
      <div style={{ paddingTop: 16 }}>
        {!readOnly && (
          <Dragger
            name="file"
            multiple
            accept={CRM_ACCEPT_ATTR}
            showUploadList={false}
            className="crm-attachment-dropzone"
            beforeUpload={file => {
              const error = validateCrmFile(file);
              if (error) {
                const limitMb = isCrmVideo(file.name) ? CRM_MAX_VIDEO_MB : CRM_MAX_FILE_MB;
                message.error(`${file.name}: ${t(error, { size: limitMb })}`);
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
            customRequest={async ({ file, onSuccess, onError }) => {
              const ok = await upload(file as File);
              if (ok) onSuccess?.({}, new XMLHttpRequest());
              else onError?.(new Error('Upload failed'));
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('ClickOrDragFileToUpload')}</p>
            <p className="ant-upload-hint">
              {t('FileSizeHint', { size: CRM_MAX_FILE_MB })} · {t('VideoSizeLimit', { size: CRM_MAX_VIDEO_MB })}
            </p>
          </Dragger>
        )}

        <Table
          className="crm-table"
          style={{ marginTop: 16 }}
          dataSource={rows}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: <Empty description={<Text type="secondary">{t('NoAttachmentsYet')}</Text>} /> }}
        />
      </div>
    </Spin>
  );
});

export default CrmAttachmentsTab;
